import { useState } from 'react';
import { callApi } from './api';
import { checkPII, replacePII } from './piiCheck';
import { copyText } from './copyText';
import { buildConferenceWordBlob, downloadBlob } from './wordExport';
import WordPreview from './WordPreview';

const THINKING_KEY = 'gj_thinking_mode';
// 部署名・氏名・進行・記録・参加者は、ここ（フォーム）とWordプレビューの
// 両方で編集できる（同じwordMetaを更新するため、どちらで直しても揃う）。
const WORD_FIELDS = [
  { key: 'department', label: '部署名' },
  { key: 'name', label: '氏名' },
  { key: 'datetime', label: '日時' },
  { key: 'place', label: '場所' },
  { key: 'facilitator', label: '進行' },
  { key: 'recorder', label: '記録' },
  { key: 'attendees', label: '参加者' },
];
const EMPTY_WORD_META = { department: '', name: '', datetime: '', place: '', facilitator: '', recorder: '', attendees: '' };
const PLACE_OPTIONS = ['SITA１階地域交流ルーム', 'SITA２階デイホール', '本館２階ホール', '本館３階会議室'];
const PLACE_OTHER = '__other__';
const WEEKDAY_JA = ['日', '月', '火', '水', '木', '金', '土'];
const REIWA_START = new Date(2019, 4, 1); // 令和元年5月1日。それより前は年号がずれるため西暦のまま扱う。

function eraYearLabel(y, m, d) {
  if (new Date(y, m - 1, d) < REIWA_START) return `${y}年`;
  const n = y - 2018;
  return `令和${n === 1 ? '元' : n}年`;
}
function formatJPDate(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  const dow = WEEKDAY_JA[new Date(y, m - 1, d).getDay()];
  return `${eraYearLabel(y, m, d)}${m}月${d}日（${dow}）`;
}

function loadThinking() {
  try {
    const v = localStorage.getItem(THINKING_KEY);
    return v === 'DEFAULT' ? 'DEFAULT' : 'LOW';
  } catch {
    return 'LOW';
  }
}

/**
 * 貼り付け→個人情報チェック→生成→結果表示、の一連。
 * 担当者会議録用・各種会議用で共通のため切り出してある。
 * 呼び出し側で `key={templateId}` を付けると、選択中の部署/種別が変わるたびに
 * 入力内容・結果がリセットされる（前の選択の書きかけを次に持ち越さないため）。
 */
export default function GenerationPanel({ templateId, templateLabel, wordExport, templateItems }) {
  const [inputText, setInputText] = useState('');
  const [thinking, setThinking] = useState(loadThinking);
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState('');
  const [result, setResult] = useState(null);
  const [copiedKey, setCopiedKey] = useState('');

  // 生成前でもプレビュー欄の骨組み（項目名）を見せられるよう、
  // 生成結果が無い間はテンプレートの項目一覧を代わりに使う。
  const fallbackItemOrder = (templateItems || []).map((i) => i.label);
  const itemOrder = result?.itemOrder ?? fallbackItemOrder;
  const items = result?.items ?? {};

  const [wordFormOpen, setWordFormOpen] = useState(() => Boolean(wordExport));
  const [wordMeta, setWordMeta] = useState(EMPTY_WORD_META);
  const [wordBuilding, setWordBuilding] = useState(false);
  const [wordError, setWordError] = useState('');
  const [placeOther, setPlaceOther] = useState(false);
  const [metaDate, setMetaDate] = useState('');
  const [metaStart, setMetaStart] = useState('');
  const [metaEnd, setMetaEnd] = useState('');

  function changeThinking(value) {
    setThinking(value);
    try { localStorage.setItem(THINKING_KEY, value); } catch { /* 保存できなくても支障なし */ }
  }

  function handleReplacePII() {
    setInputText((prev) => replacePII(prev));
  }

  async function handleGenerate() {
    if (!templateId || !inputText.trim() || generating) return;
    setGenerating(true);
    setGenError('');
    setResult(null);
    try {
      const data = await callApi('generate', { templateId, inputText, thinking });
      setResult(data);
      setWordFormOpen(true);
    } catch (err) {
      setGenError(err.message);
    } finally {
      setGenerating(false);
    }
  }

  async function handleCopyItem(label, value) {
    if (!value) return;
    const ok = await copyText(value);
    setCopiedKey(ok ? label : `${label}__failed`);
    setTimeout(() => setCopiedKey((k) => (k.startsWith(label) ? '' : k)), 1500);
  }

  async function handleCopyAll() {
    if (!result) return;
    const text = result.itemOrder
      .map((label) => `${label}\n${(result.items[label] || '').trim()}`)
      .join('\n\n');
    const ok = await copyText(text);
    setCopiedKey(ok ? '__all__' : '__all__failed');
    setTimeout(() => setCopiedKey((k) => (k.startsWith('__all__') ? '' : k)), 1500);
  }

  function updateWordMeta(key, value) {
    setWordMeta((prev) => ({ ...prev, [key]: value }));
  }

  // 氏名の入力を終えた時点（フォーカスが外れたとき）で記録欄が空なら、
  // 同じ名前を自動で入れておく（記録者=本人であることが多いため）。
  // onChangeの中（キー入力のたび）で別項目を更新すると、日本語入力中の
  // 変換（IME）が壊れる不具合が起きたため、onBlurに変更した。
  function handleNameBlur() {
    setWordMeta((prev) => (prev.recorder ? prev : { ...prev, recorder: prev.name }));
  }

  function applyDatetime(date, start, end) {
    const dateLabel = date ? formatJPDate(date) : '';
    const timePart = start && end ? `${start}～${end}` : start ? `${start}～` : '';
    updateWordMeta('datetime', [dateLabel, timePart].filter(Boolean).join('　'));
  }

  function handlePlaceSelect(value) {
    if (value === PLACE_OTHER) {
      setPlaceOther(true);
    } else {
      setPlaceOther(false);
      updateWordMeta('place', value);
    }
  }

  function updateResultItem(label, value) {
    setResult((prev) => {
      const base = prev ?? { itemOrder: fallbackItemOrder, items: {} };
      return { ...base, items: { ...base.items, [label]: value } };
    });
  }

  async function handleDownloadWord() {
    if (!result || wordBuilding) return;
    setWordBuilding(true);
    setWordError('');
    try {
      const blob = await buildConferenceWordBlob(
        { ...wordMeta, meetingName: wordExport?.meetingName ?? templateLabel },
        result.itemOrder,
        result.items,
      );
      const filename = `${(wordExport?.meetingName ?? templateLabel)}_会議報告書.docx`;
      downloadBlob(blob, filename);
    } catch (err) {
      setWordError('Wordファイルの作成に失敗しました。');
    } finally {
      setWordBuilding(false);
    }
  }

  const foundPII = checkPII(inputText);

  return (
    <div className="gen-stack">
      <div className="editor-card reveal">
        <label className="editor-label" htmlFor="gen-input">
          {templateLabel}の文字起こしを貼り付けてください
        </label>
        <textarea
          id="gen-input"
          className="editor-textarea"
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          placeholder="ここに文字起こしテキストを貼り付けます"
          disabled={generating}
        />
        <div className="editor-meta">
          <span className="editor-count">{inputText.length.toLocaleString()} 文字</span>
        </div>
        {foundPII.length > 0 && (
          <div className="pii-warn" role="alert">
            <p className="pii-warn-text">
              氏名らしい表記が見つかりました：{foundPII.slice(0, 8).join('、')}
            </p>
            <button type="button" className="pii-replace-btn" onClick={handleReplacePII}>
              検出した名前をA様・B様に置き換える
            </button>
          </div>
        )}

        <div className="thinking-toggle">
          <label className={thinking === 'LOW' ? 'is-active' : ''}>
            <input type="radio" name="thinking" value="LOW" checked={thinking === 'LOW'} onChange={() => changeThinking('LOW')} />
            高速・低コスト
          </label>
          <label className={thinking === 'DEFAULT' ? 'is-active' : ''}>
            <input type="radio" name="thinking" value="DEFAULT" checked={thinking === 'DEFAULT'} onChange={() => changeThinking('DEFAULT')} />
            通常
          </label>
        </div>

        <button className="enter" type="button" disabled={!inputText.trim() || generating} onClick={handleGenerate}>
          {generating ? '作成中…' : '議事録を作成'}
        </button>
        {genError && <p className="pin-error" role="alert">{genError}</p>}
      </div>

      {(result || wordExport) && (
        <div className="gen-layout">
          <div className="result-card reveal">
            <div className="result-head">
              <h2 className="result-title">作成結果</h2>
              <div className="result-actions">
                {wordExport && (
                  <button className="copy-all-btn" type="button" onClick={() => setWordFormOpen((v) => !v)}>
                    {wordFormOpen ? '閉じる' : '会議情報を入力'}
                  </button>
                )}
                {result && (
                  <button className="copy-all-btn" type="button" onClick={handleCopyAll}>
                    {copiedKey === '__all__' ? 'コピーしました'
                      : copiedKey === '__all__failed' ? 'コピーできませんでした'
                      : '全内容をコピー'}
                  </button>
                )}
              </div>
            </div>

            {wordExport && wordFormOpen && (
              <div className="word-form">
                <p className="word-form-note">
                  会議名は「{wordExport.meetingName ?? templateLabel}」として出力されます。残りの項目を入力してください（空欄でも出力できます）。
                </p>
                <div className="word-form-grid">
                  {WORD_FIELDS.map((f) => {
                    if (f.key === 'datetime') {
                      return (
                        <label className="word-form-field" key={f.key}>
                          <span>{f.label}</span>
                          <div className="word-datetime-pickers">
                            <input
                              type="date"
                              aria-label="日付を選択"
                              value={metaDate}
                              onChange={(e) => { setMetaDate(e.target.value); applyDatetime(e.target.value, metaStart, metaEnd); }}
                            />
                            <div className="word-time-range">
                              <input
                                type="time"
                                aria-label="開始時刻"
                                value={metaStart}
                                onChange={(e) => { setMetaStart(e.target.value); applyDatetime(metaDate, e.target.value, metaEnd); }}
                              />
                              <span>～</span>
                              <input
                                type="time"
                                aria-label="終了時刻"
                                value={metaEnd}
                                onChange={(e) => { setMetaEnd(e.target.value); applyDatetime(metaDate, metaStart, e.target.value); }}
                              />
                            </div>
                          </div>
                        </label>
                      );
                    }
                    if (f.key === 'place') {
                      const selectValue = placeOther ? PLACE_OTHER
                        : PLACE_OPTIONS.includes(wordMeta.place) ? wordMeta.place : '';
                      return (
                        <label className="word-form-field" key={f.key}>
                          <span>{f.label}</span>
                          <select value={selectValue} onChange={(e) => handlePlaceSelect(e.target.value)}>
                            <option value="" disabled hidden>選択してください</option>
                            {PLACE_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                            <option value={PLACE_OTHER}>その他（直接入力）</option>
                          </select>
                          {placeOther && (
                            <input
                              type="text"
                              className="word-place-custom"
                              value={wordMeta.place}
                              onChange={(e) => updateWordMeta('place', e.target.value)}
                              placeholder="場所を入力"
                            />
                          )}
                        </label>
                      );
                    }
                    return (
                      <label className="word-form-field" key={f.key}>
                        <span>{f.label}</span>
                        <input
                          type="text"
                          value={wordMeta[f.key]}
                          onChange={(e) => updateWordMeta(f.key, e.target.value)}
                          onBlur={f.key === 'name' ? handleNameBlur : undefined}
                        />
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
            {itemOrder.map((label) => {
              const value = items[label] || '';
              const isEmpty = !value.trim();
              return (
                <div className="result-row" key={label}>
                  <div className="result-row-head">
                    <span className="result-row-label">{label}</span>
                    <button
                      className="copy-item-btn"
                      type="button"
                      disabled={isEmpty}
                      onClick={() => handleCopyItem(label, value.trim())}
                    >
                      {copiedKey === label ? 'コピーしました'
                        : copiedKey === `${label}__failed` ? 'コピーできませんでした'
                        : 'この項目をコピー'}
                    </button>
                  </div>
                  {!wordExport && (
                    <textarea
                      className="editor-textarea result-row-body"
                      value={value}
                      placeholder="（該当なし）"
                      onChange={(e) => updateResultItem(label, e.target.value)}
                    />
                  )}
                </div>
              );
            })}
            {result?.seconds != null && (
              <p className="usage-line">
                {thinking === 'DEFAULT' ? '通常' : '高速・低コスト'} / {result.seconds.toFixed(1)}秒
              </p>
            )}
          </div>

          {wordExport && (
            <div className="preview-card reveal">
              <div className="preview-card-head">
                <h2 className="result-title">Wordプレビュー</h2>
                <button className="enter word-download-btn" type="button" disabled={wordBuilding || !result} onClick={handleDownloadWord}>
                  {wordBuilding ? '作成中…' : 'Wordでダウンロード'}
                </button>
              </div>
              <p className="preview-note">
                実際のWordファイルには承認印欄も含まれます。ここでは内容を確認しやすいよう省略しています。
                {!result && '　議事録を作成すると、ここに反映されます。'}
              </p>
              {wordError && <p className="pin-error" role="alert">{wordError}</p>}
              <WordPreview
                meetingName={wordExport.meetingName ?? templateLabel}
                meta={wordMeta}
                onMetaChange={updateWordMeta}
                onNameBlur={handleNameBlur}
                itemOrder={itemOrder}
                items={items}
                onItemChange={updateResultItem}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
